/**
 * Home — single search-bar landing page.
 *
 * Per the 2026-05-29 UX research: a small role pill above the search bar
 * lets the user declare whether they're "Buying" (default) or "I own
 * this property". The choice persists in a cookie (see useRole) and
 * navigates the user to /property/:id?intent=sell when they're the
 * owner, so PropertyFile defaults to the Seller's Pack CTA. The buyer
 * path keeps the canonical /property/:id URL.
 *
 * No sign-up gate on lookup — public records are public.
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AddressInput, { Address } from '../components/AddressInput';
import RolePill from '../components/RolePill';
import { useRole } from '../hooks/useRole';
import { api, ApiClientError } from '../utils/api';
import { trackEvent } from '../utils/analytics';

/**
 * Convert AddressInput's free-form fallback (string) into a structured
 * Address. The backend canonicaliser tolerates "Unknown" components, but
 * we still try to parse comma-separated input the way PlanningCheck.tsx
 * does to keep the lookup successful when the user typed a full address.
 */
function coerceToAddress(input: Address | string): Address {
  if (typeof input !== 'string') return input;
  const parts = input.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 3) {
    return {
      line1: parts[0],
      city: parts[parts.length - 2],
      postcode: parts[parts.length - 1],
    };
  }
  if (parts.length === 2) {
    return { line1: parts[0], city: 'Unknown', postcode: parts[1] };
  }
  return { line1: input, city: 'Unknown', postcode: 'Unknown' };
}

export default function Home() {
  const navigate = useNavigate();
  const { role, setRole } = useRole();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddressSubmit = async (input: Address | string) => {
    setError(null);
    setSubmitting(true);
    try {
      const address = coerceToAddress(input);
      trackEvent({
        name: 'address_entered',
        properties: { role: role === 'owner' ? 'seller' : 'buyer' },
      });
      const { propertyId } = await api.lookupProperty(address);
      // Owner intent appends ?intent=sell so PropertyFile renders the
      // Seller's Pack as the primary CTA. Buyer intent keeps the canonical
      // URL clean (intent=buy is the default in PropertyFile).
      const target =
        role === 'owner'
          ? `/property/${encodeURIComponent(propertyId)}?intent=sell`
          : `/property/${encodeURIComponent(propertyId)}`;
      navigate(target);
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to look up that address. Try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>UK Property Checks — Find a Property | UKPropertyChecks</title>
        <meta
          name="description"
          content="Look up any UK property. See planning history, flood risk, EPC, and tax flags. Buyer's Report £29. Seller's Pack £79."
        />
        <link rel="canonical" href="https://ukpropertychecks.co.uk/" />
        <meta property="og:title" content="UK property records: planning, EPC, flood, tax" />
        <meta
          property="og:description"
          content="Public records on any UK property. Buyer's Report £29. Seller's Pack £79."
        />
        <meta property="og:url" content="https://ukpropertychecks.co.uk/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://ukpropertychecks.co.uk/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="UK property records: planning, EPC, flood, tax" />
        <meta
          name="twitter:description"
          content="Public records on any UK property. Buyer's Report £29. Seller's Pack £79."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'UKPropertyChecks',
            url: 'https://ukpropertychecks.co.uk',
            logo: 'https://ukpropertychecks.co.uk/favicon.svg',
            description:
              'UK property records and document preparation: Buyer\'s Report £29, Seller\'s Pack £79',
            areaServed: { '@type': 'Country', name: 'United Kingdom' },
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'support@ukpropertychecks.co.uk',
              contactType: 'customer support',
              availableLanguage: 'English',
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'UKPropertyChecks',
            url: 'https://ukpropertychecks.co.uk',
            description:
              'UK property records and document preparation: Buyer\'s Report £29, Seller\'s Pack £79',
          })}
        </script>
      </Helmet>

      <main>
        {/*
         * Hero — asymmetric two-column layout on md+:
         *  - Left column: h1 + supporting paragraph (top-aligned, reads first)
         *  - Right column: search-bar card
         * On <md the columns stack (h1 first, then search). The page background
         * is slate-50; hero sits on white to anchor it visually, with a
         * border-b dividing it from the slate-50 cards section below.
         */}
        <section className="bg-white border-b border-slate-200">
          <div className="container-main section-spacing">
            <div className="grid gap-10 md:grid-cols-12 md:gap-12 items-start">
              {/* Left: headline + supporting paragraph */}
              <div className="md:col-span-5 lg:col-span-5">
                <h1 className="leading-[1.1] text-balance">
                  Check a UK property before you offer. Or before you list it.
                </h1>
                <p className="mt-5 text-lg font-normal leading-relaxed text-slate-700 max-w-prose">
                  {role === 'owner'
                    ? 'Prepare your TA6, TA7, and LPE1 disclosure pack from public records and a guided questionnaire. Sourced from the Environment Agency, MHCLG, the EPC Register, and your own evidence.'
                    : 'Public records — planning, flood, EPC, tax flags — on any address. Sourced from the Environment Agency, MHCLG, and the EPC Register.'}
                </p>
              </div>

              {/* Right: role pill + search-bar card */}
              <div className="md:col-span-7 lg:col-span-7">
                <div className="mb-3">
                  <RolePill role={role} onChange={setRole} />
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm md:p-7">
                  <AddressInput
                    label={
                      role === 'owner'
                        ? 'Address of the property you own'
                        : 'Property address or postcode'
                    }
                    helperText={
                      role === 'owner'
                        ? 'Enter the postcode of the property you want to prepare disclosure for.'
                        : 'Enter a UK postcode to find addresses, or type the full address.'
                    }
                    buttonText={
                      submitting
                        ? 'Looking up…'
                        : role === 'owner'
                          ? 'Find my property'
                          : 'Find this property'
                    }
                    onSubmit={handleAddressSubmit}
                  />
                  {error && (
                    <p className="mt-3 text-sm text-red-700" role="alert">
                      {error}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Product summary — slate-50 ground (page bg) creates a quiet step
            down from the white hero. The border-b on the hero is the divider. */}
        <section className="container-main section-spacing">
          <div className="max-w-4xl mx-auto">
            <h2 className="mb-2">The two reports</h2>
            <p className="text-base text-slate-700 mb-10 max-w-prose">
              One product for buyers, one for sellers. Same evidence base, different
              output.
            </p>

            <div className="grid gap-6 sm:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-7">
                <h3 className="text-lg font-semibold mb-3 leading-snug">Buyer's Report — £29</h3>
                <p className="text-sm leading-6 text-slate-700 mb-4">
                  Pre-offer due diligence on any UK property: planning history, flood risk,
                  EPC, and tax/regulatory flags. Your solicitor's searches (£250–£350) come
                  after offer accepted — this is what to check before you offer.
                </p>
                <ul className="text-sm leading-6 text-slate-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-700 mt-0.5" aria-hidden="true">•</span>
                    Full planning history from local-authority records
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-700 mt-0.5" aria-hidden="true">•</span>
                    Flood risk profile (Environment Agency)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-700 mt-0.5" aria-hidden="true">•</span>
                    EPC current + potential rating with recommendations
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-700 mt-0.5" aria-hidden="true">•</span>
                    Downloadable PDF
                  </li>
                </ul>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-7">
                <h3 className="text-lg font-semibold mb-3 leading-snug">Seller's Pack — £79</h3>
                <p className="text-sm leading-6 text-slate-700 mb-4">
                  Solicitor-ready TA6, TA7, and LPE1 forms from a guided questionnaire and
                  uploaded evidence. Sign in by email when you're ready to upload — takes 30
                  seconds, no password.
                </p>
                <ul className="text-sm leading-6 text-slate-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-700 mt-0.5" aria-hidden="true">•</span>
                    TA6, TA7, and LPE1 form generation
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-700 mt-0.5" aria-hidden="true">•</span>
                    Guided disclosure questionnaire
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-700 mt-0.5" aria-hidden="true">•</span>
                    Official-format PDF with provenance trail
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-10">
              <Link
                to="/how-it-works"
                className="text-sm font-semibold text-emerald-700 hover:text-emerald-800"
              >
                Read the full process →
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
